// Aplica no store as ações que o agente decidiu (recebidas de POST /ai/agent).
// Alvos vêm por id (preferido, vindo de list_nodes) OU por título (resolvido aqui).

import { useStore } from '../store/useStore.js';
import { CATEGORIES, NODE_TYPES } from '@synapse/shared';

export interface AgentAction {
  tool:
    | 'create_node'
    | 'create_task'
    | 'link_nodes'
    | 'rename_node'
    | 'delete_node'
    | 'add_note'
    | 'recolor_node'
    | 'set_category'
    | 'undo';
  args: Record<string, unknown>;
}

type Category = (typeof CATEGORIES)[number];
type NodeType = (typeof NODE_TYPES)[number];

const BATCH_CONFIRM_THRESHOLD = 15;

const COLOR_MAP: Record<string, string> = {
  cyan: '#00f5ff', ciano: '#00f5ff', azul: '#00f5ff',
  magenta: '#ff00e5', rosa: '#ff00e5', pink: '#ff00e5',
  amarelo: '#fff200', yellow: '#fff200',
  verde: '#39ff14', green: '#39ff14',
  laranja: '#ff9500', orange: '#ff9500',
  roxo: '#9d4edd', purple: '#9d4edd', violeta: '#9d4edd',
  vermelho: '#ff4d6a', red: '#ff4d6a',
};

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim() ? v.trim() : undefined;
}

function resolveColor(c: string): string | undefined {
  const t = c.trim();
  if (/^#[0-9a-f]{6}$/i.test(t)) return t;
  return COLOR_MAP[t.toLowerCase()];
}

// Resolve um nó por id (exato) ou título (exato case-insensitive; senão prefixo único).
function findNodeId(target: string): string | undefined {
  const nodes = useStore.getState().nodes;
  const byId = nodes.find((n) => n.id === target);
  if (byId) return byId.id;
  const want = target.toLowerCase();
  const exact = nodes.find((n) => n.label.toLowerCase() === want);
  if (exact) return exact.id;
  const prefix = nodes.filter((n) => n.label.toLowerCase().startsWith(want));
  return prefix.length === 1 ? prefix[0]!.id : undefined;
}

function labelOf(id: string): string {
  return useStore.getState().nodes.find((n) => n.id === id)?.label ?? id;
}

function spread(): { x: number; y: number } {
  return { x: (Math.random() - 0.5) * 320, y: (Math.random() - 0.5) * 320 };
}

// Executa a lista de ações. Retorna resumo pt-BR. Pede confirmação se for um lote grande.
export function applyAgentActions(actions: AgentAction[]): string {
  const store = useStore.getState();
  const done: string[] = [];
  const skipped: string[] = [];

  const mutating = actions.filter((a) => a.tool !== 'undo');
  if (
    mutating.length > BATCH_CONFIRM_THRESHOLD &&
    typeof window !== 'undefined' &&
    !window.confirm(`O agente vai aplicar ${mutating.length} ações no grafo. Continuar?`)
  ) {
    return '⚠ Lote cancelado pelo usuário.';
  }

  for (const action of actions) {
    try {
      switch (action.tool) {
        case 'create_node': {
          const label = str(action.args.label);
          if (!label) break;
          const category = (CATEGORIES as readonly string[]).includes(String(action.args.category))
            ? (action.args.category as Category)
            : 'idea';
          const type = (NODE_TYPES as readonly string[]).includes(String(action.args.type))
            ? (action.args.type as NodeType)
            : 'idea';
          const parent = str(action.args.parent);
          const parentId = parent ? findNodeId(parent) : undefined;
          const pos = spread();
          store.addNode({ label, category, type, parentId, x: pos.x, y: pos.y });
          done.push(`criou nó "${label}"`);
          break;
        }
        case 'create_task': {
          const label = str(action.args.label);
          if (!label) break;
          const parent = str(action.args.parent);
          const parentId = parent ? findNodeId(parent) : undefined;
          const pos = spread();
          store.addNode({ label, category: 'execution', type: 'text', parentId, x: pos.x, y: pos.y });
          done.push(`criou tarefa "${label}"`);
          break;
        }
        case 'link_nodes': {
          const from = str(action.args.from);
          const to = str(action.args.to);
          if (!from || !to) break;
          const fromId = findNodeId(from);
          const toId = findNodeId(to);
          if (!fromId || !toId) {
            skipped.push(`ligação ${from} → ${to} (nó não encontrado)`);
            break;
          }
          const kind = action.args.kind === 'wiki' ? 'wiki' : 'synapse';
          store.createLink(fromId, toId, kind);
          done.push(`ligou ${from} → ${to}`);
          break;
        }
        case 'rename_node': {
          const from = str(action.args.from);
          const to = str(action.args.to);
          if (!from || !to) break;
          const id = findNodeId(from);
          if (!id) {
            skipped.push(`renomear "${from}" (não encontrado)`);
            break;
          }
          store.updateNode(id, { label: to });
          done.push(`renomeou "${from}" para "${to}"`);
          break;
        }
        case 'add_note': {
          const target = str(action.args.target);
          const content = str(action.args.content);
          if (!target || !content) break;
          const id = findNodeId(target);
          if (!id) {
            skipped.push(`anotar "${target}" (não encontrado)`);
            break;
          }
          const node = useStore.getState().nodes.find((n) => n.id === id);
          const prev = (node?.content ?? '').toString();
          const mode = action.args.mode === 'replace' ? 'replace' : 'append';
          const next = mode === 'replace' ? content : prev ? `${prev}\n${content}` : content;
          store.updateNode(id, { content: next });
          done.push(`anotou em "${labelOf(id)}"`);
          break;
        }
        case 'recolor_node': {
          const target = str(action.args.target);
          const color = str(action.args.color);
          if (!target || !color) break;
          const id = findNodeId(target);
          if (!id) {
            skipped.push(`recolorir "${target}" (não encontrado)`);
            break;
          }
          const hex = resolveColor(color);
          if (!hex) {
            skipped.push(`cor inválida "${color}"`);
            break;
          }
          store.updateNode(id, { color: hex });
          done.push(`recoloriu "${labelOf(id)}"`);
          break;
        }
        case 'set_category': {
          const target = str(action.args.target);
          const cat = String(action.args.category);
          if (!target || !(CATEGORIES as readonly string[]).includes(cat)) break;
          const id = findNodeId(target);
          if (!id) {
            skipped.push(`categoria "${target}" (não encontrado)`);
            break;
          }
          store.updateNode(id, { category: cat as Category });
          done.push(`categoria de "${labelOf(id)}" → ${cat}`);
          break;
        }
        case 'delete_node': {
          const label = str(action.args.label) ?? str(action.args.target);
          if (!label) break;
          const id = findNodeId(label);
          if (!id) {
            skipped.push(`apagar "${label}" (não encontrado)`);
            break;
          }
          const ok = typeof window !== 'undefined' && window.confirm(`Apagar o nó "${labelOf(id)}"?`);
          if (!ok) {
            skipped.push(`apagar "${label}" (cancelado)`);
            break;
          }
          store.deleteNode(id);
          done.push(`apagou "${label}"`);
          break;
        }
        case 'undo': {
          store.undo();
          done.push('desfez última ação');
          break;
        }
      }
    } catch {
      skipped.push(`falha em ${action.tool}`);
    }
  }

  const parts: string[] = [];
  if (done.length) parts.push(`✓ ${done.join(', ')}`);
  if (skipped.length) parts.push(`⚠ pulado: ${skipped.join('; ')}`);
  return parts.join('\n');
}
